import React, { useState, useEffect } from 'react';
import RubricScoringService, {
  RubricScoringStats,
  RubricScoringResult,
} from '../../../_services/RubricScoringService';
import './RubricEvaluationButton.css';

interface RubricEvaluationButtonProps {
  onEvaluationComplete?: () => void;
}

const RubricEvaluationButton: React.FC<RubricEvaluationButtonProps> = ({
  onEvaluationComplete,
}) => {
  const [stats, setStats] = useState<RubricScoringStats | null>(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<RubricScoringResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  // Load statistics on component mount
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const response = await RubricScoringService.getScoringStats();
      if (response.success && response.data) {
        setStats(response.data);
      } else {
        setError(response.error || 'Failed to load statistics');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load statistics');
    } finally {
      setLoadingStats(false);
    }
  };

  const handleEvaluateSubmissions = async () => {
    if (!window.confirm(
      `This will evaluate ${stats?.unscoredWithRubrics || 0} submissions using AI. Continue?`
    )) {
      return;
    }

    setIsEvaluating(true);
    setError(null);
    setResults(null);

    try {
      const response = await RubricScoringService.scanAndEvaluateSubmissions();

      if (response.success && response.data) {
        setResults(response.data);
        setShowResults(true);

        // Refresh stats after evaluation
        await loadStats();

        // Notify parent component
        if (onEvaluationComplete) {
          onEvaluationComplete();
        }

        alert(
          `Evaluation Complete!\n\n` +
          `Total Processed: ${response.data.totalProcessed}\n` +
          `Successfully Scored: ${response.data.successfullyScored}\n` +
          `Skipped: ${response.data.skipped}\n` +
          `Errors: ${response.data.errors}`
        );
      } else {
        setError(response.message || response.error || 'Evaluation failed');
        alert(`Evaluation Failed: ${response.message || response.error}`);
      }
    } catch (err: any) {
      const errorMsg = err.message || 'An unexpected error occurred';
      setError(errorMsg);
      alert(`Error: ${errorMsg}`);
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleCloseResults = () => {
    setShowResults(false);
    setResults(null);
  };

  if (loadingStats) {
    return (
      <div className="rubric-evaluation-button-container">
        <button className="rubric-evaluation-button" disabled>
          <span className="button-icon">⏳</span>
          Loading...
        </button>
      </div>
    );
  }

  return (
    <div className="rubric-evaluation-button-container">
      <div className="rubric-stats-card">
        <div className="stats-row">
          <div className="stat-item">
            <span className="stat-label">Unscored Submissions:</span>
            <span className="stat-value">{stats?.totalUnscoredSubmissions || 0}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">With Rubrics:</span>
            <span className="stat-value highlight">{stats?.unscoredWithRubrics || 0}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Recently Graded:</span>
            <span className="stat-value">{stats?.recentlyGradedCount || 0}</span>
          </div>
        </div>

        <button
          className="rubric-evaluation-button"
          onClick={handleEvaluateSubmissions}
          disabled={isEvaluating || (stats?.unscoredWithRubrics || 0) === 0}
        >
          {isEvaluating ? (
            <>
              <span className="button-icon spinning">⚙️</span>
              Evaluating Submissions...
            </>
          ) : (
            <>
              <span className="button-icon">🎯</span>
              Evaluate Submissions with Rubrics
            </>
          )}
        </button>

        <button
          className="refresh-stats-button"
          onClick={loadStats}
          disabled={loadingStats || isEvaluating}
        >
          🔄 Refresh Stats
        </button>
      </div>

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      {showResults && results && (
        <div className="results-modal-overlay" onClick={handleCloseResults}>
          <div className="results-modal" onClick={(e) => e.stopPropagation()}>
            <div className="results-modal-header">
              <h3>Rubric Evaluation Results</h3>
              <button className="close-button" onClick={handleCloseResults}>×</button>
            </div>

            <div className="results-summary">
              <div className="summary-item">
                <span className="summary-label">Total Processed:</span>
                <span className="summary-value">{results.totalProcessed}</span>
              </div>
              <div className="summary-item success">
                <span className="summary-label">Successfully Scored:</span>
                <span className="summary-value">{results.successfullyScored}</span>
              </div>
              <div className="summary-item warning">
                <span className="summary-label">Skipped:</span>
                <span className="summary-value">{results.skipped}</span>
              </div>
              <div className="summary-item error">
                <span className="summary-label">Errors:</span>
                <span className="summary-value">{results.errors}</span>
              </div>
            </div>

            <div className="results-details">
              <h4>Details:</h4>
              <div className="results-table">
                {results.details.map((detail, index) => (
                  <div key={index} className={`result-row ${detail.status}`}>
                    <span className="result-submission-id">
                      Submission #{detail.submissionId}
                    </span>
                    <span className="result-status">{detail.status}</span>
                    {detail.score !== undefined && (
                      <span className="result-score">
                        {detail.score}/{detail.maxScore} ({detail.grade})
                      </span>
                    )}
                    {detail.reason && (
                      <span className="result-reason">{detail.reason}</span>
                    )}
                    {detail.error && (
                      <span className="result-error">{detail.error}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="results-modal-footer">
              <button className="close-button-footer" onClick={handleCloseResults}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RubricEvaluationButton;
